import { useMemo } from 'react';
import { useHistory, useLocation } from 'quartz-routing/react-router';

export const KEY_PREFIX = 'eco_';

/** `"push"` (default) creates a new entry on the history stack,
    allowing the user to navigate back to the previous state. Use
    this for user-initiated changes that should be undoable.

    `"replace"` is the opposite - the user cannot get back to
    the previous state. Use this for redirects and other automated
    changes. */
export type HistoryMethod = 'push' | 'replace';

export function useSearchParams() {
  const history = useHistory();
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);

    const updateHistory = (historyMethod: HistoryMethod = 'push') => {
      const searchString = params.toString();

      const path = searchString
        ? `${location.pathname}?${searchString}`
        : location.pathname;

      history[historyMethod](path);
    };

    return {
      /**
       * Get a URL param, optionally restricting the allowed values.
       * If the value is not in `allowedValues`, `undefined` is returned.
       */
      get: <T extends readonly string[]>(
        key: string | undefined,
        { allowedValues }: { allowedValues?: T } = {}
      ): T[number] | undefined => {
        if (!key) return undefined;

        const value = params.get(KEY_PREFIX + key);

        // return empty string if outside of allowed values
        if (allowedValues && !allowedValues.includes(value ?? ''))
          return undefined;

        // Ensure null from params.get() becomes undefined
        return (value ?? undefined) as T[number] | undefined;
      },

      /**
       * Get all values for a URL parameter with the same key.
       * This is used for multi-value parameters where the same key appears multiple times.
       */
      getList: (key: string) => {
        const value = params.getAll(KEY_PREFIX + key);
        return value;
      },

      getAll: () => {
        const result: Record<string, string[]> = {};
        const uniqueKeys = new Set<string>();

        for (const key of params.keys()) {
          if (!key.includes(KEY_PREFIX)) continue;
          uniqueKeys.add(key.replace(KEY_PREFIX, ''));
        }

        for (const key of uniqueKeys) {
          result[key] = params.getAll(KEY_PREFIX + key);
        }

        return result;
      },

      keys: () =>
        Array.from(
          new Set(
            Array.from(params.keys()).filter((key) =>
              key.startsWith(KEY_PREFIX)
            )
          )
        ).map((key) => (key as string).replace(KEY_PREFIX, '')),

      /**
       * @param defaultValue - If `value` matches this, the param is removed instead.
       * @param historyMethod - Defaults to `"push"`. Can also be set to `"replace"`. See `HistoryMethod` docs for more.
       * @param defer - Set this to `true` to avoid updating the history. Useful for batching updates, where all but the last one are deferred.
       */
      set: (
        key: string,
        value: string,
        {
          defaultValue,
          historyMethod,
          defer,
        }: {
          defaultValue?: string;
          historyMethod?: HistoryMethod;
          defer?: boolean;
        } = {}
      ) => {
        if (!value || value === defaultValue) {
          params.delete(KEY_PREFIX + key);
        } else {
          params.set(KEY_PREFIX + key, value);
        }

        if (!defer) updateHistory(historyMethod);
      },

      /**
       * Fully replaces a list with the provided values. Preexisting values will be deleted.
       * @param defer - Set this to `true` to avoid updating the history. Useful for batching updates, where all but the last one are deferred.
       */
      setList: (
        key: string,
        values: string[],
        {
          historyMethod,
          defer,
        }: { historyMethod?: HistoryMethod; defer?: boolean } = {}
      ) => {
        params.delete(KEY_PREFIX + key);

        values.forEach((value) => {
          params.append(KEY_PREFIX + key, value);
        });

        if (!defer) updateHistory(historyMethod);
      },

      delete: (
        key: string,
        {
          historyMethod,
          defer,
        }: { historyMethod?: HistoryMethod; defer?: boolean } = {}
      ) => {
        params.delete(KEY_PREFIX + key);
        if (!defer) updateHistory(historyMethod);
      },

      /**
       * Deletes a value from a list of values for the given key.
       * Other values will remain.
       */
      deleteFromList: (
        key: string,
        valueToDelete: string,
        {
          historyMethod,
          defer,
        }: { historyMethod?: HistoryMethod; defer?: boolean } = {}
      ) => {
        const prefixedKey = KEY_PREFIX + key;
        const currentValues = params.getAll(prefixedKey);
        const newValues = currentValues.filter((v) => v !== valueToDelete);

        // Delete all existing instances of the key
        params.delete(prefixedKey);

        // Append the remaining values
        newValues.forEach((value) => {
          params.append(prefixedKey, value);
        });

        if (!defer) updateHistory(historyMethod);
      },

      /**
       * Manually triggers a history update for all pending changes.
       *
       * Use this when you've made multiple changes with `defer: true` and need to
       * commit them all at once. If you're making sequential updates and know which
       * is last, you can skip `defer` on the final call instead.
       *
       * Example: Updating multiple params based on different conditions, then
       * calling this once at the end to apply all changes in a single history entry.
       */
      updateHistory,
    };
  }, [history, location]);
}
